import { useState } from "react";
import { formatDate, todayLocal } from "../../api/dates";
import { useSaveGeneral, type GeneralIn, type InspectionDetail } from "../../api/inspections";
import VisitTimes from "./VisitTimes";

type Form = {
  work_order_no: string;
  region_id: string;
  cluster_id: string;
  site_id: string;
  chamber_name: string;
  site_type: string;
  work_type: string;
  inspection_date: string;
  team_id: string;
  team_name: string;
};

function bodyOf(form: Form): GeneralIn {
  const num = (v: string) => (v === "" ? null : Number(v));
  return {
    work_order_no: form.work_order_no,
    region_id: num(form.region_id),
    cluster_id: num(form.cluster_id),
    site_id: num(form.site_id),
    chamber_name: form.chamber_name || null,
    site_type: form.site_type || null,
    work_type: form.work_type || null,
    inspection_date: form.inspection_date || null,
    team_id: num(form.team_id),
    team_name: form.team_name || null,
  };
}

/** Section 1. The one thing the field operators must say is what kind of
 * work the visit is; everything else -- where, who, when -- usually came with
 * the route plan and is shown as facts, with the form behind "Change these
 * details" for the visit that did not. Every change saves as it is made. */
export default function GeneralTab({ detail }: { detail: InspectionDetail }) {
  const head = detail.inspection;
  const save = useSaveGeneral(head.id);
  const canEdit = detail.actions.can_edit;
  const siteTypes = detail.options.lookups.site_types.values;
  const [form, setForm] = useState<Form>({
    work_order_no: head.work_order_no,
    region_id: head.region_id === null ? "" : String(head.region_id),
    cluster_id: head.cluster_id === null ? "" : String(head.cluster_id),
    site_id: head.site_id === null ? "" : String(head.site_id),
    chamber_name: head.chamber_name ?? "",
    // The QCP's own words for the place are not one of the checklist's three
    // site types; shown blank rather than posted back and refused.
    site_type: head.site_type !== null && siteTypes.includes(head.site_type) ? head.site_type : "",
    work_type: head.work_type ?? "",
    inspection_date: head.inspection_date ?? "",
    team_id: head.team_id === null ? "" : String(head.team_id),
    team_name: head.team_name ?? "",
  });
  const [saved, setSaved] = useState<Form>(form);

  function commit(next: Form) {
    setForm(next);
    if (JSON.stringify(next) === JSON.stringify(saved)) {
      return;
    }
    save.mutate(bodyOf(next), { onSuccess: () => setSaved(next) });
  }
  const set = <K extends keyof Form>(key: K, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const choose = <K extends keyof Form>(key: K, value: string) => commit({ ...form, [key]: value });
  const blur = () => commit(form);

  const region = detail.options.regions.find((r) => String(r.id) === form.region_id)?.name ?? null;
  const cluster = detail.options.clusters.find((c) => String(c.id) === form.cluster_id)?.name ?? null;
  const site = detail.options.sites.find((s) => String(s.id) === form.site_id)?.name ?? (form.chamber_name || null);
  const team = detail.options.teams.find((t) => String(t.id) === form.team_id)?.name ?? (form.team_name || null);
  const known = [
    ["Site", site],
    ["Area", [region, cluster].filter(Boolean).join(" · ") || null],
    ["Field operators", team],
    ["Date", form.inspection_date ? formatDate(form.inspection_date) : null],
    ["Works order", form.work_order_no || null],
  ] as const;

  const qcp = detail.qcp;
  const qcpFacts = [
    ["Manufacturer", qcp.manufacturer],
    ["Valve", qcp.valve_description],
    ["DN (scope)", qcp.dn_scope === null ? null : String(qcp.dn_scope)],
    ["DN (BOQ)", qcp.dn_boq === null ? null : String(qcp.dn_boq)],
    ["Equipment no.", qcp.equipment_no],
    ["Functional location", qcp.functional_location],
  ].filter((pair): pair is [string, string] => pair[1] !== null && pair[1] !== "");

  return (
    <div className="insp-section">
      <h3>1 · General</h3>

      <section className="insp-work-type" aria-labelledby="work-type-label">
        <h4 id="work-type-label">
          What kind of work is this visit? {form.work_type === "" ? <span className="insp-needed">needed</span> : null}
        </h4>
        <div className="insp-choices" role="group" aria-labelledby="work-type-label">
          {detail.options.work_types.map((wt) => (
            <button
              key={wt.value}
              type="button"
              className="insp-choice"
              aria-pressed={form.work_type === wt.value}
              disabled={!canEdit || save.isPending}
              onClick={() => choose("work_type", wt.value)}
            >
              {wt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="insp-subsection">
        <dl className="insp-facts">
          {known.map(([label, value]) => (
            <div key={label} className="fact-pair">
              <dt>{label}</dt>
              <dd>{value ?? "—"}</dd>
            </div>
          ))}
        </dl>
        {head.work_order_from_route ? <p className="insp-hint">From the accepted route plan.</p> : null}
        <details className="insp-more">
          <summary>Change these details</summary>
          <div className="insp-fields">
            <label>
              1.1 Works order no.
              <input value={form.work_order_no} onChange={(e) => set("work_order_no", e.target.value)} onBlur={blur} readOnly={!canEdit || head.work_order_locked} />
            </label>
            <label>
              1.2 Region
              <select value={form.region_id} onChange={(e) => choose("region_id", e.target.value)} disabled={!canEdit}>
                <option value="">—</option>
                {detail.options.regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              1.3 Cluster
              <select value={form.cluster_id} onChange={(e) => choose("cluster_id", e.target.value)} disabled={!canEdit}>
                <option value="">—</option>
                {detail.options.clusters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              1.4 Site
              <select value={form.site_id} onChange={(e) => choose("site_id", e.target.value)} disabled={!canEdit}>
                <option value="">—</option>
                {detail.options.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Chamber name (when no site)
              <input value={form.chamber_name} onChange={(e) => set("chamber_name", e.target.value)} onBlur={blur} readOnly={!canEdit} />
            </label>
            <label>
              1.7 Inspection date
              <input type="date" value={form.inspection_date} onChange={(e) => choose("inspection_date", e.target.value)} readOnly={!canEdit} />
            </label>
            <label>
              1.8 Field operators
              <select value={form.team_id} onChange={(e) => choose("team_id", e.target.value)} disabled={!canEdit}>
                <option value="">—</option>
                {detail.options.teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Team name (free text)
              <input value={form.team_name} onChange={(e) => set("team_name", e.target.value)} onBlur={blur} readOnly={!canEdit} />
            </label>
          </div>
        </details>
      </section>

      <section className="insp-subsection">
        <label className="insp-single">
          1.6 Pump station, reservoir or treatment plant? <span className="insp-hint">Leave blank for a valve chamber.</span>
          <select value={form.site_type} onChange={(e) => choose("site_type", e.target.value)} disabled={!canEdit}>
            <option value="">—</option>
            {siteTypes.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
      </section>

      {head.kind === "pm41" ? (
        <VisitTimes
          inspectionId={head.id}
          day={head.inspection_date ?? todayLocal()}
          arrivedAt={head.arrived_at ?? null}
          departedAt={head.departed_at ?? null}
          canEdit={canEdit}
        />
      ) : null}
      {save.isError ? <p className="notice bad">Not saved: {save.error.message}</p> : null}

      {qcpFacts.length > 0 ? (
        <details className="insp-more insp-subsection">
          <summary>From the QCP{qcp.valve_description ? ` · ${qcp.valve_description}` : ""}</summary>
          <dl className="insp-facts">
            {qcpFacts.map(([label, value]) => (
              <div key={label} className="fact-pair">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <p className="insp-hint">Shown for reference. Nothing measured is prefilled from the document.</p>
        </details>
      ) : null}
    </div>
  );
}
